import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { isIOS, isAndroid } from 'react-device-detect';
import { API_CONFIG } from '../config/api';
import StarryBackground from './StarryBackground';
import { useI18n } from '../i18n';

const WaitlistPage = () => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    deviceType: 'android',
    interests: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    try {
      // Rileva dispositivo automaticamente se non specificato
      const deviceType = formData.deviceType || 
        (isIOS ? 'ios' : isAndroid ? 'android' : 'desktop');
      
      // Chiamata alla Lambda Waitlist dedicata (Function URL)
      const response = await fetch(API_CONFIG.WAITLIST_ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          request_type: 'waitlist_signup',
          email: formData.email.trim(),
          name: formData.name.trim() || '',
          deviceType: deviceType,
          interests: formData.interests.trim() || '',
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          deepLinkPath: window.location.pathname,
          timestamp: new Date().toISOString(),
          source: 'webapp'
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setStatus('success');
        setMessage(result.message || '🎉 Perfetto! Ti avviseremo appena WanderBuddy sarà disponibile per il tuo dispositivo.');
        
        // Analytics/tracking se necessario
        if (window.gtag) {
          window.gtag('event', 'waitlist_signup', {
            device_type: deviceType,
            email_domain: formData.email.split('@')[1]
          });
        }
        
        // Reset form dopo successo
        setTimeout(() => {
          setFormData({ email: '', name: '', deviceType: 'android', interests: '' });
        }, 2000);
      } else {
        setStatus('error');
        setMessage(result.message || '❌ Si è verificato un errore. Riprova più tardi.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('❌ Si è verificato un errore. Riprova tra qualche minuto.');
      console.error('Waitlist subscription error:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (status === 'error') setStatus('idle'); // Reset error state on input
  };

  return (
    <>
      <Helmet>
        <title>{t('waitlist.title')} | WanderBuddy</title>
        <meta name="description" content={t('waitlist.subtitle')} />
        <meta property="og:title" content={`${t('waitlist.title')} | WanderBuddy`} />
        <meta property="og:description" content={t('waitlist.subtitle')} />
      </Helmet>

      {/* Sfondo stellato */}
      <StarryBackground />

      <div className="sora-layout">
        <motion.main 
          className="sora-main"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Header */}
          <motion.div 
            className="sora-app-icon"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div style={{ 
              width: '140px', 
              height: '140px', 
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '4rem',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              📧
            </div>
          </motion.div>
          
          <motion.h1 
            className="sora-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {t('waitlist.title')}
          </motion.h1>
          
          <motion.p 
            className="sora-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {t('waitlist.subtitle')}
          </motion.p>

          {/* Status Alert */}
          {status === 'success' && (
            <motion.div 
              className="sora-alert sora-alert-success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {message}
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div 
              className="sora-alert sora-alert-error"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {message}
            </motion.div>
          )}

          {/* Form */}
          <motion.form 
            className="sora-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
          <div className="sora-form-group">
            <label className="sora-form-label" htmlFor="email">
              {t('waitlist.email')} *
            </label>
            <input
              id="email"
              type="email"
              className="sora-form-input"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
            />
          </div>

          <div className="sora-form-group">
            <label className="sora-form-label" htmlFor="name">
              {t('waitlist.name')}
            </label>
            <input
              id="name"
              type="text"
              className="sora-form-input"
              placeholder="Your name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </div>

          <div className="sora-form-group">
            <label className="sora-form-label" htmlFor="deviceType">
              {t('waitlist.device')}
            </label>
            <select
              id="deviceType"
              className="sora-form-input"
              value={formData.deviceType}
              onChange={(e) => handleInputChange('deviceType', e.target.value)}
            >
              <option value="android">📱 Android</option>
              <option value="ios">🍎 iOS (other devices)</option>
              <option value="web">💻 Web Version</option>
              <option value="all">🌐 All devices</option>
            </select>
          </div>

          <div className="sora-form-group">
            <label className="sora-form-label" htmlFor="interests">
              {t('waitlist.interests')}
            </label>
            <textarea
              id="interests"
              className="sora-form-input sora-form-textarea"
              placeholder="E.g: cultural trips, adventures, romantic getaways, business travel..."
              value={formData.interests}
              onChange={(e) => handleInputChange('interests', e.target.value)}
              rows={3}
            />
          </div>

          <motion.button
            type="submit"
            className="sora-submit-btn"
            disabled={status === 'loading' || !formData.email}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {status === 'loading' && <div className="loading-spinner"></div>}
            {status === 'loading' ? t('waitlist.submitting') : t('waitlist.submit')}
          </motion.button>
          </motion.form>

          {/* Info aggiuntive */}
          <motion.div 
            className="sora-info-box"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <strong>What happens next?</strong>
            <br />
            We'll notify you when WanderBuddy is available for your device.
            <br />
            <small style={{ opacity: 0.7, marginTop: '0.5rem', display: 'block' }}>
              No spam, just important updates. Unsubscribe anytime.
            </small>
          </motion.div>

          {/* Link back to home */}
          <motion.div 
            style={{ marginTop: '2rem' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <a 
              href="/"
              className="sora-back-link"
            >
              {t('waitlist.back')}
            </a>
          </motion.div>

          {/* Footer */}
          <motion.footer 
            className="sora-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {t('common.footer')}
          </motion.footer>
        </motion.main>
      </div>
    </>
  );
};

export default WaitlistPage;
